import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getReport, getPublicReport, downloadReportPdf, downloadPublicReportPdf } from '../lib/api';
import { isAuthenticated, getUser } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { formatDate, formatCurrency, downloadBlob } from '../lib/utils';
import { ArrowLeft, Download, AlertCircle, ExternalLink, Lock } from 'lucide-react';

export default function ReportView() {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const authenticated = isAuthenticated();

  useEffect(() => {
    loadReport();
  }, [reportId]);

  const loadReport = async () => {
    setLoading(true);
    setError(null);
    try {
      // Try authenticated endpoint first if logged in
      if (authenticated) {
        const response = await getReport(reportId);
        setReport(response.data);
      } else {
        // Try public endpoint
        const response = await getPublicReport(reportId);
        setReport(response.data);
      }
    } catch (err) {
      if (err.response?.status === 403) {
        setError('This report is not publicly shared. Please login to view.');
      } else if (err.response?.status === 404) {
        setError('Report not found.');
      } else {
        setError('Failed to load report.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!report) return;
    try {
      let response;
      if (authenticated) {
        response = await downloadReportPdf(report.id || reportId);
      } else {
        response = await downloadPublicReportPdf(reportId);
      }
      downloadBlob(response.data, `report-${report.report_id}.pdf`);
    } catch (err) {
      alert('Failed to download report');
    }
  };

  const formatOutputValue = (key, value) => {
    if (value === null || value === undefined) return '-';
    if (Array.isArray(value)) {
      return value.length > 0 ? value.join(', ') : 'None';
    }
    if (key.includes('loss') || key.includes('recovered') || key.includes('gain') || key.includes('cost')) {
      return formatCurrency(value);
    }
    return String(value);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-zinc-500">Loading report...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white" data-testid="report-error">
        <header className="border-b border-zinc-200">
          <div className="container mx-auto px-8 py-6">
            <Link to="/" className="header-logo text-xl">AI Accountability Standards</Link>
          </div>
        </header>
        <main className="container mx-auto px-8 py-12">
          <div className="max-w-md mx-auto text-center">
            {error.includes('not publicly') ? (
              <Lock className="w-12 h-12 text-zinc-400 mx-auto mb-4" />
            ) : (
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            )}
            <h1 className="text-xl font-semibold mb-2">Access Denied</h1>
            <p className="text-zinc-600 mb-6">{error}</p>
            {error.includes('not publicly') && (
              <Link to="/org/login">
                <Button className="btn-primary rounded-none">
                  Login to View
                </Button>
              </Link>
            )}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="report-view-page">
      {/* Header */}
      <header className="border-b border-zinc-200">
        <div className="container mx-auto px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <Link to="/" className="header-logo text-xl">AI Accountability Standards</Link>
            </div>
            <nav className="flex items-center gap-6">
              <Link to="/registry" className="nav-link">Registry</Link>
              <Link to="/tools" className="nav-link">Tools</Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-8 py-12">
        <div className="max-w-3xl mx-auto">
          {/* Breadcrumb */}
          <div className="breadcrumb">
            {authenticated ? (
              <Link to="/org/dashboard" className="hover:text-zinc-700 flex items-center gap-1">
                <ArrowLeft className="w-4 h-4" />
                Dashboard
              </Link>
            ) : (
              <Link to="/tools" className="hover:text-zinc-700 flex items-center gap-1">
                <ArrowLeft className="w-4 h-4" />
                Tools
              </Link>
            )}
            <span className="breadcrumb-separator">/</span>
            <span>Report</span>
          </div>

          {/* Report Header */}
          <div className="border border-zinc-200 mb-6">
            <div className="p-6 border-b border-zinc-200 bg-zinc-50">
              <h1 className="text-2xl font-semibold mb-1" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Verified Assessment Report
              </h1>
              <p className="text-sm text-zinc-500">
                Generated using AI Accountability Standards verified instruments
              </p>
            </div>

            <div className="divide-y divide-zinc-200">
              <div className="p-4 flex justify-between">
                <span className="text-sm text-zinc-500">Report ID</span>
                <span className="font-mono text-sm font-medium" data-testid="report-id">{report?.report_id}</span>
              </div>
              <div className="p-4 flex justify-between">
                <span className="text-sm text-zinc-500">Organization</span>
                <span className="font-medium" data-testid="report-org">{report?.org_name}</span>
              </div>
              <div className="p-4 flex justify-between">
                <span className="text-sm text-zinc-500">Tool</span>
                <span data-testid="report-tool">{report?.tool_name}</span>
              </div>
              <div className="p-4 flex justify-between">
                <span className="text-sm text-zinc-500">Standard</span>
                <span>
                  {report?.standard_title}
                  <span className="text-xs text-zinc-400 ml-2 font-mono">({report?.standard_id})</span>
                </span>
              </div>
              <div className="p-4 flex justify-between">
                <span className="text-sm text-zinc-500">Generated</span>
                <span className="font-mono text-sm">{formatDate(report?.created_at)}</span>
              </div>
              <div className="p-4 flex justify-between">
                <span className="text-sm text-zinc-500">Public Share</span>
                <Badge variant="outline" className="rounded-none">
                  {report?.is_public_share_enabled ? 'Enabled' : 'Disabled'}
                </Badge>
              </div>
            </div>
          </div>

          {/* Inputs */}
          <div className="border border-zinc-200 mb-6">
            <div className="p-4 bg-zinc-50 border-b border-zinc-200">
              <h2 className="text-sm font-medium uppercase tracking-wide text-zinc-600">Inputs</h2>
            </div>
            <div className="divide-y divide-zinc-100">
              {Object.entries(report?.inputs_json || {}).map(([key, value]) => (
                <div key={key} className="p-4 flex justify-between">
                  <span className="text-sm text-zinc-500">{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                  <span className="font-mono text-sm">{String(value)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Outputs */}
          <div className="border border-emerald-200 bg-emerald-50 mb-6">
            <div className="p-4 bg-emerald-100 border-b border-emerald-200">
              <h2 className="text-sm font-medium uppercase tracking-wide text-emerald-800">Results</h2>
            </div>
            <div className="divide-y divide-emerald-100">
              {Object.entries(report?.outputs_json || {}).map(([key, value]) => (
                <div key={key} className="p-4 flex justify-between bg-white">
                  <span className="text-sm text-zinc-500">{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                  <span className="font-mono text-sm font-medium" data-testid={`output-${key}`}>
                    {formatOutputValue(key, value)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Download */}
          <Button
            onClick={handleDownload}
            className="w-full btn-primary rounded-none h-11"
            data-testid="download-report-pdf"
          >
            <Download className="w-4 h-4 mr-2" />
            Download PDF Report
          </Button>

          {/* Disclaimer */}
          <div className="mt-6 p-4 bg-zinc-50 border border-zinc-200">
            <p className="text-xs text-zinc-500">
              <strong>Disclaimer:</strong> Accountability declaration indicates documented governance alignment with published standards. 
              It does not imply endorsement, approval, regulatory compliance, or performance guarantees. Estimates only.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
